"use client";

/**
 * Build a `WingmanClient` for a signed-in chat send.
 *
 * Browser → same-origin BFF with the httpOnly session cookie. The BFF
 * decrypts the cookie and forwards Authorization: Bearer to Wingman. The
 * JWT never reaches page JavaScript.
 *
 * Every request rides the `/api/wingman/*` route handler (see
 * `services/wingman/bff.ts`). Attachment bytes are the one thing that never
 * do: the v2 adapter asks Wingman for a single-use upload grant through the
 * route handler and PUTs the file straight to Wingman with no credentials,
 * so the append body stays small and the 4.5 MB function request cap is
 * never in play. Legacy has no grant flow; its streaming append with
 * inlined files keeps going directly to the legacy host as before.
 *
 * Public embeds do not use this helper; they call Wingman directly with
 * X-Org-Id / X-Session-Id and `credentials: "omit"`.
 *
 * A 401 here means either a dead session or a stale access token inside
 * the cookie. Attachment sends call `tryRefreshToken()` first so the grant
 * request does not have to be retried after a large decode. JSON sends rely
 * on the 401-retry recipe.
 */

import { extractErrorShape, logAuthEvent, newAuthEventId } from "@/lib/authLogging";
import { makeWingmanPosthogFailureHook } from "@/lib/wingman-tracing";
import { WingmanClient, WingmanApiError, type WingmanRouting } from "@/services/wingman";
import { signedInWingmanTransport } from "@/services/wingman/bff";
import { resolveWingmanRouting } from "@/services/wingman/routing";
import { tryRefreshToken } from "@/lib/api";
import { WINGMAN_GENERATION_TIMEOUT_MS } from "@/services/wingman/generation";

const chatFailureHook = makeWingmanPosthogFailureHook("gailgpt");

export interface GetMessageClientOptions {
  hasAttachments: boolean;
  /** Pinned route for conversation-scoped requests. */
  routing?: WingmanRouting;
}

export async function getMessageClient(
  options: GetMessageClientOptions
): Promise<WingmanClient> {
  if (options.hasAttachments) {
    // Roll a fresh access token into the cookie *before* uploading. If the
    // cookie's AT is stale, Wingman can't reject until it's read the full
    // request body — which for an attachment means we've already pushed
    // 10s of MB over the wire. The WingmanClient's 401-retry recipe would
    // then re-upload the same bytes a second time with the refreshed
    // cookie. Refreshing eagerly here trades one cheap `/api/auth/refresh`
    // round-trip (~200ms) for the chance of a multi-MB double-upload.
    //
    // Non-attachment sends do NOT need this: the JSON body is tiny, so
    // letting the 401-retry recipe handle stale-AT recovery costs only
    // a few extra ms and avoids the proactive refresh on the hot path.
    //
    // Result intentionally ignored: a failed refresh still lets the
    // upload try with whatever cookie we have. `tryRefreshToken` itself
    // fires `notifySessionExpired()` only on Auth0-rejected refresh.
    await tryRefreshToken();
  }
  const routing = options.routing ?? resolveWingmanRouting(false);
  return new WingmanClient({
    timeoutMs: WINGMAN_GENERATION_TIMEOUT_MS,
    onRequestFailure: chatFailureHook,
    ...signedInWingmanTransport(routing, { largeBody: options.hasAttachments }),
  });
}

/**
 * True if the error thrown by `WingmanClient.executeRequest` looks like a
 * 401 from the upstream Wingman API.
 */
export function isWingman401Error(error: unknown): boolean {
  return error instanceof WingmanApiError && error.status === 401;
}

const EXPIRED_EMBED_SESSION = "Session is expired or does not exist";

/**
 * Public embeds (Logan, web chat) authenticate with `X-Session-Id`. When
 * Galactus has no matching session, Wingman returns 401 with this detail.
 * The iframe should drop the header and retry anonymously rather than
 * leaving the user stuck on continue/new.
 */
export function isExpiredEmbedSessionError(error: unknown): boolean {
  if (error instanceof WingmanApiError) {
    return (
      error.status === 401 && error.responseBody.includes(EXPIRED_EMBED_SESSION)
    );
  }
  // Errors re-thrown across a stream boundary can lose their class. Match
  // the WingmanClient message shape for a 401 plus the detail so an
  // unrelated error that happens to quote the detail cannot retry.
  return (
    error instanceof Error &&
    error.message.includes("Wingman API error 401") &&
    error.message.includes(EXPIRED_EMBED_SESSION)
  );
}

/**
 * Failure handling for direct Wingman sends.
 *
 * Emits a structured `wingman_direct.failure` `auth_event` so the existing
 * log-grep workflows surface failures with an `auth_event_id` correlation.
 * Deliberately does NOT call `notifySessionExpired()` — see module-level
 * NOTE on why 401s here are ambiguous and must surface as ordinary errors.
 */
export function handleWingmanFailure(
  error: unknown,
  conversationId: string | undefined
): void {
  logAuthEvent({
    event: "wingman_direct.failure",
    auth_event_id: newAuthEventId(),
    conversation_id: conversationId,
    error: extractErrorShape(error),
  });
}
